import { useState } from "react";
import { Mail, CheckCircle2 } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import client, { apiError } from "../lib/api";
import { SOCIAL } from "../data/content";

const TOPICS = [
  { id: "fan", label: "Fan" },
  { id: "press", label: "Press" },
  { id: "licensing", label: "Licensing" },
  { id: "production", label: "Production" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "fan", message: "" });
  const [status, setStatus] = useState({ type: "idle", text: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await client.post("/contact", form);
      setStatus({ type: "ok", text: data.message });
      setForm({ name: "", email: "", topic: "fan", message: "" });
    } catch (err) {
      setStatus({ type: "err", text: apiError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-16" data-testid="contact-page">
      <Seo title="Contact" description="Fan, press, licensing and production inquiries for Laugh at L.A. and Providence Film Group." />
      <section className="spotlight-bg border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-24">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">Contact</p>
            <h1 className="mt-3 font-display text-5xl sm:text-7xl text-white neon-red">Talk to the club.</h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16 grid lg:grid-cols-[1fr_380px] gap-10">
        <Reveal>
          {status.type === "ok" ? (
            <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-10 text-center" data-testid="contact-success">
              <CheckCircle2 size={40} className="mx-auto text-emerald-400" />
              <p className="mt-4 font-display text-3xl text-white">{status.text}</p>
              <button onClick={() => setStatus({ type: "idle", text: "" })} className="mt-6 text-sm text-[#F59E0B] link-gold" data-testid="contact-send-another">
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#181824]/70 backdrop-blur-xl p-8 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name" aria-label="Your name" data-testid="contact-name"
                  className="rounded-xl border border-white/15 bg-black/40 px-5 py-3.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Your email" aria-label="Your email" data-testid="contact-email"
                  className="rounded-xl border border-white/15 bg-black/40 px-5 py-3.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]" />
              </div>
              <fieldset>
                <legend className="text-xs font-semibold uppercase tracking-widest text-gray-400">This is about</legend>
                <div className="mt-3 flex flex-wrap gap-2">
                  {TOPICS.map((t) => (
                    <button key={t.id} type="button" onClick={() => setForm({ ...form, topic: t.id })} aria-pressed={form.topic === t.id} data-testid={`contact-topic-${t.id}`}
                      className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors ${form.topic === t.id ? "border-[#F59E0B] bg-[#F59E0B]/15 text-[#F59E0B]" : "border-white/15 text-gray-300 hover:border-white/40"}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </fieldset>
              <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Your message" aria-label="Your message" data-testid="contact-message"
                className="w-full rounded-xl border border-white/15 bg-black/40 px-5 py-3.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]" />
              {status.type === "err" && <p role="alert" className="text-sm text-red-400" data-testid="contact-error">{status.text}</p>}
              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="rounded-full bg-[#E11D48] px-8 py-3.5 font-semibold text-white transition-colors hover:bg-[#F43F5E] disabled:opacity-60">
                {loading ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <aside className="rounded-2xl border border-white/10 bg-[#181824]/70 p-8 h-fit" data-testid="contact-direct">
            <h2 className="font-display text-2xl text-white">Direct lines</h2>
            <div className="mt-6 space-y-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B]">The show</p>
                <a href={`mailto:${SOCIAL.emailShow}`} className="mt-1 flex items-center gap-2 text-gray-200 hover:text-white break-all" data-testid="contact-email-show">
                  <Mail size={15} className="shrink-0 text-[#E11D48]" /> {SOCIAL.emailShow}
                </a>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B]">Providence Film Group</p>
                <a href={`mailto:${SOCIAL.emailPfg}`} className="mt-1 flex items-center gap-2 text-gray-200 hover:text-white break-all" data-testid="contact-email-pfg">
                  <Mail size={15} className="shrink-0 text-[#E11D48]" /> {SOCIAL.emailPfg}
                </a>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed border-t border-white/10 pt-5">
                Licensing and commission inquiries can also start on the Industry page — the deck and buyer one-sheet are there.
              </p>
            </div>
          </aside>
        </Reveal>
      </section>
    </main>
  );
}
